import React from 'react';
import { useState, useEffect } from 'react';
import { X, MapPin, Wrench } from 'lucide-react';
import type { Plumber } from '../../types';
import { supabase } from '../../lib/supabase';

interface State {
  id: string;
  code: string;
  name: string;
}

interface City {
  id: string;
  name: string;
  zip_codes: string[];
}

interface Service {
  id: string;
  title: string;
}

interface PlumberFormProps {
  plumber: Partial<Plumber>;
  onChange: (field: string, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function PlumberForm({ plumber, onChange, onSubmit, onCancel, isSubmitting }: PlumberFormProps) {
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedStateIds, setSelectedStateIds] = useState<string[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initForm = async () => {
      await Promise.all([loadStates(), loadServices()]);

      // If editing existing plumber, set initial state and city
      if (plumber.service_areas?.length > 0 && plumber.service_areas[0].city_id) {
        const firstArea = plumber.service_areas[0];
        const { data: cityData } = await supabase
          .from('cities')
          .select('state_id')
          .eq('id', firstArea.city_id)
          .single();

        if (cityData) {
          setSelectedStateIds([cityData.state_id]);
          setSelectedCityId(firstArea.city_id);
        }
      }

      // Set initial selected services
      if (plumber.services?.length > 0) {
        setSelectedServices(plumber.services.map(s => s.service_id));
      }
    };

    initForm();
  }, []);

  useEffect(() => {
    if (selectedStateIds.length > 0) {
      loadCities(selectedStateIds);
    } else {
      setCities([]);
    }
  }, [selectedStateIds]);

  const loadStates = async () => {
    try {
      const { data, error } = await supabase
        .from('states')
        .select('id, code, name')
        .order('name');

      if (error) throw error;
      setStates(data || []);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('id, title')
        .order('title');

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadCities = async (stateIds: string[]) => {
    if (stateIds.includes('all')) {
      try {
        const { data, error } = await supabase
          .from('cities')
          .select('id, name, zip_codes')
          .order('name');

        if (error) throw error;
        setCities(data || []);
      } catch (error) {
        console.error('Error loading all cities:', error);
      }
      return;
    }

    try {
      const { data, error } = await supabase
        .from('cities')
        .select('id, name, zip_codes')
        .in('state_id', stateIds)
        .order('name');

      if (error) throw error;
      setCities(data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(event.target.selectedOptions).map(option => option.value);
    setSelectedStateIds(selectedOptions);
    setSelectedCityId('');
    
    // If "All States" is selected
    if (selectedOptions.includes('all')) {
      // Set empty service areas to indicate serving all areas
      onChange('service_areas', []);
    } else {
      onChange('service_areas', []);
    }
  };

  const handleCityChange = (cityId: string) => {
    setSelectedCityId(cityId);
    
    if (cityId === 'all') {
      // Get all ZIP codes for selected states
      if (selectedStateIds.length > 0) {
        supabase
          .from('cities')
          .select('id, zip_codes')
          .in('state_id', selectedStateIds)
          .then(({ data: stateCities }) => {
            if (stateCities) {
              const allStateZipCodes = stateCities.flatMap(city => 
                city.zip_codes.map(zip => ({
                  zip_code: zip,
                  city_id: city.id
                }))
              );
              onChange('service_areas', allStateZipCodes);
            }
          });
      }
    } else if (cityId) {
      const selectedCity = cities.find(city => city.id === cityId);
      if (selectedCity) {
        onChange('service_areas', selectedCity.zip_codes.map(zip => ({
          zip_code: zip,
          city_id: cityId
        })));
      }
    } else {
      onChange('service_areas', []);
    }
  };

  const handleServiceChange = (serviceId: string) => {
    const newServices = selectedServices.includes(serviceId)
      ? selectedServices.filter(id => id !== serviceId)
      : [...selectedServices, serviceId];
    
    setSelectedServices(newServices);
    onChange('services', newServices.map(id => ({
      service_id: id
    })));
  };

  const handleSelectAllServices = () => {
    if (selectedServices.length === services.length) {
      setSelectedServices([]);
      onChange('services', []);
    } else {
      const allServiceIds = services.map(service => service.id);
      setSelectedServices(allServiceIds);
      onChange('services', allServiceIds.map(id => ({
        service_id: id
      })));
    }
  };

  const getServiceAreaSummary = () => {
    if (selectedStateIds.includes('all')) {
      return 'All States';
    }
    if (selectedCityId === 'all') {
      const stateNames = states
        .filter(s => selectedStateIds.includes(s.id))
        .map(s => s.name)
        .join(', ');
      return `All Cities in ${stateNames}`;
    } else if (selectedCityId) {
      const city = cities.find(c => c.id === selectedCityId);
      const state = states.find(s => s.id === city?.state_id);
      return `${city?.name}, ${state?.code}`;
    }
    return 'No area selected';
  };

  const getZipCodeCount = () => {
    if (selectedStateIds.includes('all')) {
      return cities.reduce((sum, city) => sum + city.zip_codes.length, 0);
    }
    if (selectedCityId === 'all') {
      return cities.reduce((sum, city) => sum + city.zip_codes.length, 0);
    } else if (selectedCityId) {
      const selectedCity = cities.find(city => city.id === selectedCityId);
      return selectedCity?.zip_codes.length || 0;
    }
    return 0;
  };

  const renderServiceArea = () => {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-900 flex items-center mb-2">
          <MapPin className="w-4 h-4 mr-1" />
          Service Area
          <span className="text-red-500 ml-1">*</span>
        </h4>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">
            State
          </label>
          <p className="text-sm text-gray-500 mb-2">Hold Ctrl/Cmd to select multiple states</p>
          <select 
            multiple
            value={selectedStateIds}
            onChange={handleStateChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 min-h-[120px]"
            required
          >
            <option value="all">All States</option>
            {states.map(state => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        {selectedStateIds.length > 0 && !selectedStateIds.includes('all') && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              City
            </label>
            <select
              value={selectedCityId}
              onChange={(e) => handleCityChange(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select a city</option>
              <option value="all">All Cities</option>
              {cities.map(city => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {(selectedStateIds.includes('all') || selectedCityId) && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h5 className="text-sm font-medium text-gray-700 mb-2">
              {getServiceAreaSummary()}
            </h5>
            <p className="text-sm text-gray-600 mb-2">
              Covering {getZipCodeCount()} ZIP codes
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderServices = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-gray-900 flex items-center">
            <Wrench className="w-4 h-4 mr-1" />
            Services Offered
          </h4>
          <button
            type="button"
            onClick={handleSelectAllServices}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {selectedServices.length === services.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {services.map(service => (
            <label key={service.id} className="flex items-center p-2 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                checked={selectedServices.includes(service.id)}
                onChange={() => handleServiceChange(service.id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                {service.title}
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Edit Plumber Details
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-500"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Business Name
        </label>
        <input
          type="text"
          value={plumber.business_name || ''}
          onChange={(e) => onChange('business_name', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Contact Name
        </label>
        <input
          type="text"
          value={plumber.contact_name || ''}
          onChange={(e) => onChange('contact_name', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Email
        </label>
        <input
          type="email"
          value={plumber.email || ''}
          onChange={(e) => onChange('email', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Phone
        </label>
        <input
          type="tel"
          value={plumber.phone || ''}
          onChange={(e) => onChange('phone', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Website
        </label>
        <input
          type="url"
          value={plumber.website || ''}
          onChange={(e) => onChange('website', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          value={plumber.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          License Number
        </label>
        <input
          type="text"
          value={plumber.license_number || ''}
          onChange={(e) => onChange('license_number', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Insurance Info
        </label>
        <textarea
          value={plumber.insurance_info || ''}
          onChange={(e) => onChange('insurance_info', e.target.value)}
          rows={2}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Years of Experience
        </label>
        <input
          type="number"
          value={plumber.years_experience || ''}
          onChange={(e) => onChange('years_experience', parseInt(e.target.value))}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          min="0"
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_verified"
            checked={plumber.is_verified || false}
            onChange={(e) => onChange('is_verified', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="is_verified" className="ml-2 block text-sm text-gray-900">
            Verified Plumber
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_featured"
            checked={plumber.is_featured || false}
            onChange={(e) => onChange('is_featured', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
            Featured Listing
          </label>
        </div>

        <div>
          <label htmlFor="rating" className="block text-sm font-medium text-gray-700">
            Rating (1-5)
          </label>
          <input
            type="number"
            id="rating"
            value={plumber.rating || 0}
            onChange={(e) => onChange('rating', parseFloat(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            min="1"
            max="5"
            step="0.1"
          />
        </div>
      </div>

      {renderServiceArea()}
      {renderServices()}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}